/**
 * Registration Context for managing post-registration state
 * Tracks if user just completed registration for enhanced UX
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { EmailVerificationState } from '../types/email-verification';

interface RegistrationContextType {
  // Existing properties (unchanged)
  isJustRegistered: boolean;
  setJustRegistered: (value: boolean) => void;
  registrationData: {
    timestamp: number;
    username: string;
  } | null;
  setRegistrationData: (data: { username: string } | null) => void;
  
  // NEW: Email verification properties (additive only)
  emailVerificationState: EmailVerificationState | null;
  setEmailVerificationState: (state: EmailVerificationState | null) => void;
  isEmailFirstFlowEnabled: boolean;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export const RegistrationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Existing state (unchanged)
  const [isJustRegistered, setIsJustRegistered] = useState(false);
  const [registrationData, setRegistrationDataState] = useState<{
    timestamp: number;
    username: string;
  } | null>(null);
  
  // NEW: Email verification state (additive)
  const [emailVerificationState, setEmailVerificationState] = useState<EmailVerificationState | null>(null);
  const isEmailFirstFlowEnabled = import.meta.env.VITE_EMAIL_FIRST_FLOW_ENABLED === 'true';

  const setJustRegistered = (value: boolean) => {
    setIsJustRegistered(value);
    
    // Auto-clear after 5 minutes to prevent stale state
    if (value) {
      setTimeout(() => {
        setIsJustRegistered(false);
        setRegistrationDataState(null);
      }, 5 * 60 * 1000);
    }
  };

  const setRegistrationData = (data: { username: string } | null) => {
    if (data) {
      setRegistrationDataState({
        ...data,
        timestamp: Date.now(),
      });
    } else {
      setRegistrationDataState(null);
    }
  };

  return (
    <RegistrationContext.Provider
      value={{
        // Existing (unchanged)
        isJustRegistered,
        setJustRegistered,
        registrationData,
        setRegistrationData,
        
        // NEW (additive)
        emailVerificationState,
        setEmailVerificationState,
        isEmailFirstFlowEnabled,
      }}
    >
      {children}
    </RegistrationContext.Provider>
  );
};

export const useRegistration = (): RegistrationContextType => {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};